/**
 * LE CHOIX DU THÈME — poste de travail cadastral.
 *
 * L'application n'a eu qu'un thème sombre pendant toute son existence. Un agent
 * qui instruit un dossier en plein jour, près d'une fenêtre, n'a pas le même
 * besoin qu'un géomètre qui relit un levé le soir : le socle porte désormais les
 * deux, et ceci en donne la commande.
 *
 * ⚠️ TROIS ÉTATS, PAS DEUX. « Auto » suit le système et reste le défaut : ne
 * rien choisir ne doit rien imposer. Un basculeur à deux états force un choix
 * que personne n'a demandé, et l'agent qui bascule son poste en clair à midi
 * retrouverait le cadastre en sombre.
 *
 * ⚠️ CE FICHIER EST NEUF, ET C'EST DÉLIBÉRÉ. `app.tsx` est écrit par une
 * douzaine de sessions ; y poser un état, un effet et un bouton aurait
 * multiplié les occasions de conflit. Ici, `app.tsx` ne reçoit que deux lignes.
 */
import { useEffect, useState } from 'react';

export type Theme = 'auto' | 'light' | 'dark';

const CLE = 'mowibo-cadastral-theme';
const SUIVANT: Record<Theme, Theme> = { auto: 'light', light: 'dark', dark: 'auto' };
const LIBELLE: Record<Theme, string> = {
  auto: 'Thème : système',
  light: 'Thème : clair',
  dark: 'Thème : sombre',
};

/** Lit le choix retenu. Un stockage refusé (navigation privée, poste verrouillé)
 *  ne doit pas empêcher l'application de s'afficher : on retombe sur « auto ». */
function lire(): Theme {
  try {
    const v = localStorage.getItem(CLE);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch { /* stockage indisponible : le système décide */ }
  return 'auto';
}

/** Applique le choix à la racine. `auto` RETIRE l'attribut au lieu d'en poser
 *  un : c'est l'absence de marque qui laisse `prefers-color-scheme` décider. */
export function appliquerTheme(t: Theme): void {
  const r = document.documentElement;
  if (t === 'auto') r.removeAttribute('data-theme');
  else r.setAttribute('data-theme', t);
}

export function BasculeTheme() {
  const [theme, setTheme] = useState<Theme>(lire);

  useEffect(() => {
    appliquerTheme(theme);
    try { localStorage.setItem(CLE, theme); } catch { /* sans mémoire, le choix vaut pour la session */ }
  }, [theme]);

  return (
    <button
      type="button"
      className="bascule-theme"
      onClick={() => setTheme((t) => SUIVANT[t])}
      title={`${LIBELLE[theme]} — cliquer pour ${LIBELLE[SUIVANT[theme]].toLowerCase()}`}
      aria-label={LIBELLE[theme]}
    >
      {theme === 'auto' ? (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <circle cx="8" cy="8" r="5.6" />
          <path d="M8 2.4v11.2" />
        </svg>
      ) : theme === 'light' ? (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <circle cx="8" cy="8" r="3.2" />
          <path d="M8 1.2v1.8M8 13v1.8M1.2 8h1.8M13 8h1.8M3.2 3.2l1.3 1.3M11.5 11.5l1.3 1.3M12.8 3.2l-1.3 1.3M4.5 11.5l-1.3 1.3" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <path d="M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6a5.8 5.8 0 1 0 7 7z" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

/**
 * LE THÈME EFFECTIF, POUR CE QUI NE SAIT PAS LIRE LE CSS.
 *
 * MapLibre et `<canvas>` n'acceptent pas `var()` : ils reçoivent des littéraux.
 * Une carte construite une fois garde donc les couleurs du thème qui régnait à
 * sa naissance, et basculer ne la touche pas — l'interface change autour d'un
 * fond de plan resté sombre.
 *
 * Ce crochet rend `'light'` ou `'dark'` et se met à jour sur les DEUX signaux
 * qui peuvent changer : l'attribut `data-theme` posé par le basculeur, et le
 * réglage système quand personne n'a choisi. Le mettre dans les dépendances de
 * l'effet qui construit la carte suffit : React démonte la carte, l'effet
 * rejoue, et les couleurs sont relues.
 */
export function useThemeActif(): 'light' | 'dark' {
  const calcul = (): 'light' | 'dark' => {
    const marque = document.documentElement.getAttribute('data-theme');
    if (marque === 'light' || marque === 'dark') return marque;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  };
  const [actif, setActif] = useState<'light' | 'dark'>(calcul);
  useEffect(() => {
    const relire = () => setActif(calcul());
    const obs = new MutationObserver(relire);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    mq.addEventListener('change', relire);
    return () => { obs.disconnect(); mq.removeEventListener('change', relire); };
  }, []);
  return actif;
}
